import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Phone, Building2, Shield, Clock, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";
import { useState } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const ServiceCard = ({ title, description, Icon, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="bg-[#F9F9F7] p-8 flex gap-6"
  >
    <div className="w-14 h-14 bg-[#0F4C81] rounded-full flex items-center justify-center flex-shrink-0">
      <Icon className="w-7 h-7 text-white" />
    </div>
    <div>
      <h3 className="font-heading text-2xl font-medium text-[#1A202C] mb-2">{title}</h3>
      <p className="font-body text-[#4B5563]">{description}</p>
    </div>
  </motion.div>
);

const BenefitItem = ({ title, desc, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="text-center"
  >
    <h3 className="font-heading text-lg font-medium text-white mb-1">{title}</h3>
    <p className="font-body text-white/70 text-sm">{desc}</p>
  </motion.div>
);

const CommercialGlassPage = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="pt-20">
      <SEO
        title="Storefront & Commercial Glass"
        description="Professional commercial glass services in coastal NC. Storefronts, curtain walls, aluminum doors, and glass handrails. Code-compliant installation with minimal business disruption."
        keywords="commercial glass, storefront glass, curtain walls, aluminum doors, commercial glass NC, Brunswick County storefront"
        ogImage="https://images.unsplash.com/photo-1759421965457-c5a8463b8b71?w=1200&q=80"
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-[#F9F9F7]">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
            alt="Commercial glass storefront"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="section-container relative z-10 w-full py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-[#0F4C81] font-body mb-6 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4"
            >
              Commercial Services
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-6"
            >
              Storefront & Commercial Glass
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-xl leading-relaxed text-[#4B5563] mb-8"
            >
              Your storefront is often the first impression of your business. We provide professional storefront and light commercial glazing solutions that are durable, code-compliant, and visually inviting.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A355C] text-white rounded-full px-8 h-14 font-body font-semibold">
                    Get a Free Quote
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] p-8">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
                  </DialogHeader>
                  <QuoteForm onClose={() => setQuoteOpen(false)} />
                </DialogContent>
              </Dialog>
              <a href="tel:910-457-4757">
                <Button variant="outline" className="border-2 border-[#1A202C] rounded-full px-8 h-14 font-body font-semibold">
                  <Phone className="w-5 h-5 mr-2" /> 910-457-4757
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-12 bg-[#0F4C81]">
        <div className="section-container">
          <div className="grid md:grid-cols-4 gap-8">
            <BenefitItem title="Code Compliant" desc="All installations meet local building codes" index={0} />
            <BenefitItem title="Minimal Disruption" desc="Efficient work to keep your business running" index={1} />
            <BenefitItem title="Durable Materials" desc="Commercial-grade glass and hardware" index={2} />
            <BenefitItem title="Professional Service" desc="Experienced commercial glass specialists" index={3} />
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-16"
          >
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">
              Commercial Glass Services
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#4B5563] max-w-2xl">
              We offer comprehensive commercial glazing solutions for businesses of all sizes.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <ServiceCard
              title="Storefront Windows & Doors"
              description="Professional storefront glazing that creates an inviting entrance for your customers while providing security and energy efficiency."
              Icon={Building2}
              index={0}
            />
            <ServiceCard
              title="Curtain Walls"
              description="Large-scale glass wall systems that provide stunning aesthetics and natural light for commercial buildings."
              Icon={Shield}
              index={1}
            />
            <ServiceCard
              title="Aluminum Doors & Hardware"
              description="Commercial-grade aluminum door systems with premium hardware for durability and smooth operation."
              Icon={Wrench}
              index={2}
            />
            <ServiceCard
              title="Glass Handrails"
              description="Modern glass railing systems for staircases, balconies, and commercial spaces that combine safety with elegant design."
              Icon={Clock}
              index={3}
            />
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-spacing bg-[#F9F9F7]">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl font-medium text-[#1A202C] mb-6">Additional Commercial Services</h2>
              <p className="font-body text-lg text-[#4B5563] mb-8">
                Beyond storefronts, we provide a full range of commercial glass solutions to meet your business needs.
              </p>
              <ul className="grid grid-cols-2 gap-4">
                <li className="flex items-center gap-2 font-body text-[#4B5563]">
                  <Check className="w-5 h-5 text-[#0F4C81]" /> Glass Handrails
                </li>
                <li className="flex items-center gap-2 font-body text-[#4B5563]">
                  <Check className="w-5 h-5 text-[#0F4C81]" /> Gym Mirrors
                </li>
                <li className="flex items-center gap-2 font-body text-[#4B5563]">
                  <Check className="w-5 h-5 text-[#0F4C81]" /> Office Partitions
                </li>
                <li className="flex items-center gap-2 font-body text-[#4B5563]">
                  <Check className="w-5 h-5 text-[#0F4C81]" /> Safety Glass
                </li>
                <li className="flex items-center gap-2 font-body text-[#4B5563]">
                  <Check className="w-5 h-5 text-[#0F4C81]" /> Window Replacement
                </li>
                <li className="flex items-center gap-2 font-body text-[#4B5563]">
                  <Check className="w-5 h-5 text-[#0F4C81]" /> System Repairs
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                alt="Commercial building"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#0F4C81]">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl font-medium text-white mb-4">Make a Great First Impression</h2>
          <p className="font-body text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Your storefront matters. Let us help you create an inviting, professional appearance that attracts customers.
          </p>
          <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-[#0F4C81] hover:bg-white/90 rounded-full px-8 h-14 font-body font-semibold">
                Get Your Free Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] p-8">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
              </DialogHeader>
              <QuoteForm onClose={() => setQuoteOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default CommercialGlassPage;
